import { useState, useEffect } from "react";
import { CheckCircleIcon, XIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface SnackbarProps {
    message: string;
    description?: string;
    isOpen: boolean;
    onClose: () => void;
    duration?: number;
    variant?: "success" | "error" | "info" | "warning";
    className?: string;
}

export default function Snackbar({
    message,
    description,
    isOpen,
    onClose,
    duration = 3000,
    variant = "success",
    className,
}: SnackbarProps) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(onClose, 300); // Wait for animation to complete before calling onClose
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [isOpen, duration, onClose]);

    const variantStyles = {
        success: "bg-green-50 border-green-200 text-green-800",
        error: "bg-red-50 border-red-200 text-red-800",
        info: "bg-blue-50 border-blue-200 text-blue-800",
        warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    };

    const iconColors = {
        success: "text-green-500",
        error: "text-red-500",
        info: "text-blue-500",
        warning: "text-yellow-500",
    };

    const getIcon = () => {
        switch (variant) {
            case "success":
                return <CheckCircleIcon className={`h-5 w-5 ${iconColors[variant]}`} />;

            // Add other icons for different variants if needed
            default:
                return <CheckCircleIcon className={`h-5 w-5 ${iconColors[variant]}`} />;
        }
    };

    return (
        <div
            className={cn(
                "fixed right-4 top-4 z-50 transform transition-all duration-300 ease-in-out",
                isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0",
                className
            )}
        >
            <div
                className={cn(
                    "flex w-80 items-start rounded-md border p-4 shadow-md",
                    variantStyles[variant]
                )}
                style={{
                    backgroundColor: "#ffffff",
                    color: "#000000FF",
                    border: "0.909091px solid #e2e8f0",
                }}
            >
                <div className="mr-3 flex-shrink-0">{getIcon()}</div>
                <div className="flex-1">
                    <h4 className="mb-1 font-medium">{message}</h4>
                    {description && <p className="text-sm opacity-90">{description}</p>}
                </div>
                <button
                    onClick={() => {
                        setIsVisible(false);
                        setTimeout(onClose, 300);
                    }}
                    className="ml-4 flex-shrink-0 rounded-full p-1 hover:bg-gray-200/50"
                >
                    <XIcon className="h-4 w-4 text-gray-500" />
                </button>
            </div>
        </div>
    );
}
